import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useRedesign } from '../../Components/general/RedesignShell';
import { useStatCards } from '../../Components/Home/Statistics';
import { getBulletinList } from '../../Utils/frontStageApi';
import heroImg from '../../Resources/Images/Redesign/digital-credential-hero.png';

interface BulletinPreview {
  date: string;
  title: string;
}

export const HomePage: React.FC = () => {
  const history = useHistory();
  const { openModal } = useRedesign();
  const { cards: statCards } = useStatCards();
  const [latestBulletins, setLatestBulletins] = useState<BulletinPreview[]>([]);

  useEffect(() => {
    getBulletinList(0, 1)
      .then((data) => {
        if (data?.list && Array.isArray(data.list)) {
          setLatestBulletins(data.list.slice(0, 3));
        }
      })
      .catch(() => {});
  }, []);

  // Stats calculation
  const schoolCount = statCards.find((c) => c.key === 'apply')?.unitValue || '—';
  const vcCount = statCards.find((c) => c.key === 'apply')?.totalValue || '—';
  const verifyCount = statCards.find((c) => c.key === 'verify')?.totalValue || '—';

  return (
    <>
      <section className="dc-hero">
        <div>
          <div className="dc-eyebrow">DIGITAL CREDENTIAL SERVICES</div>
          <h1>
            申請、驗證與導入
            <br />
            找到您需要的數位證明服務
          </h1>
          <p className="dc-lead">
            個人領取數位憑證、查驗數位證書，或由學校與機構導入服務，從這裡開始。
          </p>
        </div>
        <figure className="dc-hero-art">
          <img
            src={heroImg}
            width="1536"
            height="1024"
            alt="數位證書、手機憑證皮夾與驗證盾牌的藍綠色概念插畫"
          />
        </figure>
      </section>

      <div className="dc-grid">
        <article className="dc-service feature">
          <span className="dc-number">01 / CREDENTIAL</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <h2>數位憑證</h2>
          </div>
          <p>學位憑證申請與驗證、校園應用，以及學校導入服務。</p>
          <button
            type="button"
            className="dc-button primary"
            onClick={() => history.push('/vc')}
          >
            進入數位憑證
          </button>
        </article>

        <article className="dc-service">
          <span className="dc-number">02 / CERTIFICATE</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <h2>數位證書</h2>
            <span
              className="dc-label"
              style={{ color: 'var(--dc-gold)', borderColor: 'var(--dc-gold)' }}
            >
              開發中，即將推出
            </span>
          </div>
          <p>查找發證單位，進行證書驗證與非學位證書驗證。</p>
          <button
            type="button"
            className="dc-button"
            onClick={() => openModal('數位證書功能開發中', '此功能仍在開發中，敬請期待！')}
          >
            探索數位證書
          </button>
        </article>

        <article className="dc-service">
          <span className="dc-number">03 / BATCH VERIFY</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <h2>批次驗證</h2>
          </div>
          <p>大量證書查驗、結果檢視，以及 API 相關服務。</p>
          <button
            type="button"
            className="dc-button"
            onClick={() => { window.location.href = '/bv/' }}
          >
            探索批次驗證
          </button>
        </article>
      </div>

      <div className="dc-statrow">
        <div>
          <span>支援學校數</span>
          <strong>{schoolCount !== '0' && schoolCount !== '--' ? schoolCount : '—'}</strong>
          <small>{schoolCount !== '0' && schoolCount !== '--' ? '已介接學校' : '待串接正式資料'}</small>
        </div>
        <div>
          <span>數位憑證發證數</span>
          <strong>{vcCount !== '0' && vcCount !== '--' ? vcCount : '—'}</strong>
          <small>{vcCount !== '0' && vcCount !== '--' ? '累積發行次數' : '待串接正式資料'}</small>
        </div>
        <div>
          <span>數位憑證驗證數</span>
          <strong>{verifyCount !== '0' && verifyCount !== '--' ? verifyCount : '—'}</strong>
          <small>{verifyCount !== '0' && verifyCount !== '--' ? '累積驗證次數' : '(服務上線後提供)'}</small>
        </div>
      </div>

      <div className="dc-bottom">
        <section>
          <div className="dc-heading-row">
            <h2>最新消息</h2>
            <button
              type="button"
              className="dc-link"
              onClick={() => history.push('/news')}
            >
              查看全部 ↗
            </button>
          </div>
          {latestBulletins.length === 0 ? (
            <p className="dc-empty">公告資料將顯示於此。</p>
          ) : (
            latestBulletins.map((b, idx) => (
              <div key={idx} className="dc-row">
                <span style={{ color: 'var(--dc-ink)', fontWeight: 500 }}>{b.title}</span>
                <span style={{ fontSize: '13px', color: 'var(--dc-muted)' }}>{b.date}</span>
              </div>
            ))
          )}
        </section>

        <section>
          <h2>認識中心與服務</h2>
          <div className="dc-row">
            <button
              type="button"
              className="dc-link"
              onClick={() => history.push('/about')}
            >
              中心介紹 ↗
            </button>
            <button
              type="button"
              className="dc-link"
              onClick={() => history.push('/about/partners')}
            >
              合作單位 ↗
            </button>
          </div>
          <div className="dc-row">
            <button
              type="button"
              className="dc-link"
              onClick={() => history.push('/about/faq')}
            >
              常見問題 ↗
            </button>
            <button
              type="button"
              className="dc-link"
              onClick={() => history.push('/about/contact')}
            >
              聯絡我們 ↗
            </button>
          </div>
        </section>
      </div>
    </>
  );
};

export default HomePage;
